/**
 * Locale config — shared between bootstrap, formatters, the picker, and
 * the pre-paint script.
 *
 * Adding a new locale: add the slug here, add it to USER_LOCALES on the
 * BE (`src/schemas/locales.ts`), ship the migration that ALTER TYPEs
 * `user_locale`, drop a new `src/i18n/locales/<slug>/*.json` set, and
 * the picker auto-renders the new option from this list.
 *
 * `en-XA` is the pseudo-locale used for QA — generated client-side from
 * `en/` at build time, NOT a real locale. It's surfaced in the picker
 * only when `import.meta.env.DEV === true`.
 */

import type { Locale } from '@/api/types-bridge'
import { LOCALES } from '@/api/types-bridge'

export { LOCALES }
export type { Locale }

export const DEFAULT_LOCALE: Locale = 'en'

/**
 * Display metadata for each locale shown in the picker. The native name
 * intentionally appears in its own locale (Spanish users see "Español",
 * not "Spanish") — this is the standard accessibility pattern.
 */
export const LOCALE_DISPLAY: Record<Locale, { nativeName: string; englishName: string }> = {
  en: { nativeName: 'English', englishName: 'English' },
  es: { nativeName: 'Español', englishName: 'Spanish' },
}

/**
 * Pseudo-locale slug — only resolved when `import.meta.env.DEV` is true.
 * Generated by `scripts/generate-pseudo-locale.mjs` from `en/` at FE
 * build time; wraps every value in brackets/accents and adds 30% length
 * expansion so QA can spot missed extractions and truncation bugs.
 */
export const PSEUDO_LOCALE = 'en-XA' as const
export type PseudoLocale = typeof PSEUDO_LOCALE

/** Locales surfaced in the picker — pseudo-locale only in dev. */
export const PICKER_LOCALES: readonly (Locale | PseudoLocale)[] =
  import.meta.env.DEV === true ? [...LOCALES, PSEUDO_LOCALE] : LOCALES

/**
 * Namespaces map 1:1 to feature folders under `src/features/**` (plus
 * `routes`, `components`, `errors`, `validation`, `formats`). Each
 * namespace is its own JSON file under `src/i18n/locales/<locale>/`.
 *
 * Lazy-loaded by `i18next-http-backend` from `/locales/<locale>/<ns>.json`
 * — the `public/locales/` directory in dev (and the dist root in prod)
 * mirrors `src/i18n/locales/`. The Vite static-asset config in
 * `vite.config.ts` copies the directory at build time.
 */
export const I18N_NAMESPACES = [
  'common',
  'auth',
  'board',
  'master-board',
  'settings',
  'time',
  'comments',
  'labels',
  'uploads',
  'chat',
  'notifications',
  'wiki',
  'reports',
  'agents',
  'errors',
  'validation',
  'formats',
] as const

export type I18nNamespace = (typeof I18N_NAMESPACES)[number]

export const DEFAULT_NAMESPACE: I18nNamespace = 'common'

/**
 * RTL locales — empty today; the codebase is logical-property-ready
 * (Tailwind v4 native), so adding 'ar' or 'he' here flips `<html dir>`
 * and the layout mirrors automatically. Kept as a Set for O(1) check
 * during the pre-paint script + `useLocale`.
 */
export const RTL_LOCALES: ReadonlySet<string> = new Set<string>([
  // 'ar', 'he', 'fa', 'ur',  -- add when an RTL locale onboards
])

export const isRtl = (locale: string): boolean => {
  // Match by primary tag so `ar-EG`, `he-IL`, etc. resolve correctly.
  const primary = locale.toLowerCase().split('-')[0] ?? ''
  return RTL_LOCALES.has(primary)
}
